import React from 'react';
import { ListSubheader, MenuItem, Typography } from '@mui/material';

// Short labels for the kind of money a category holds
const KIND_LABELS = {
  spending: 'Spending',
  income: 'Income',
  transfer: 'Transfer',
  investment: 'Investment',
  refund: 'Refund',
  asset: 'Big purchase',
};

/**
 * Menu items for a category <Select>: categories under their group headings,
 * each with its kind (Spending, Transfer, Investment ...). Plain name lists
 * (strings) are shown flat. The kind label is hidden in the selected value by
 * the theme (MuiSelect override).
 * @param {Array<{ id, name, groupName?, kind? } | string>} categories
 */
export const categoryMenuItems = (categories) => {
  const list = Array.isArray(categories) ? categories : [];
  if (list.length === 0 || typeof list[0] === 'string') {
    return list.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>);
  }

  // Groups in the order their first category appears (the API sorts by sort order)
  const groups = new Map();
  for (const category of list) {
    const group = category.groupName || 'Other';
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(category);
  }

  const items = [];
  for (const [group, members] of groups) {
    items.push(
      <ListSubheader key={`group-${group}`} sx={{ lineHeight: '32px', fontWeight: 600 }}>
        {group}
      </ListSubheader>
    );
    for (const category of members) {
      items.push(
        <MenuItem key={category.id || category.name} value={category.name} sx={{ pl: 3 }}>
          <span style={{ flex: 1 }}>{category.name}</span>
          {category.kind && (
            <Typography className="category-kind-label" variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {KIND_LABELS[category.kind] || category.kind}
            </Typography>
          )}
        </MenuItem>
      );
    }
  }
  return items;
};

/** Only categories that count as spending (budgets) */
export const spendingCategories = (categories) =>
  (Array.isArray(categories) ? categories : []).filter(c => typeof c === 'string' || !c.kind || c.kind === 'spending');
